/**
 * ClientGoalsPanel - goal tree + management actions for a single client.
 *
 * Owns: useGoalTree, the loading/empty/no-goals/tree states, <GoalTree>, and the
 * Add/Edit/Change-status/Delete/Import modals (+ their open/close state). This is
 * the single implementation shared by GoalsManagementPage's right detail panel and
 * ClientDetailDrawer's "Goals" section, so the two surfaces can never drift.
 */

import { ReactNode, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Plus, Upload, Target as TargetIcon } from 'lucide-react';
import type { GoalNode } from '@upskill/shared';
import { Button } from '../../components/ui/Button';
import { useGoalTree } from '../../hooks/useGoals';
import { useAuthStore } from '../../stores/authStore';
import { GoalTree } from './GoalTree';
import { GoalFormModal } from './GoalFormModal';
import { ChangeStatusModal } from './ChangeStatusModal';
import { DeleteGoalDialog } from './DeleteGoalDialog';
import { GoalImportModal } from './GoalImportModal';
import { NEXT_LEVEL } from './statusMeta';
import { media } from '../../styles/media';

// ==================== Styled Components ====================

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  align-items: center;
  flex-wrap: wrap;

  ${media.tablet} {
    width: 100%;

    button {
      flex: 1;
    }
  }
`;

export const Card = styled.div`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  padding: ${({ theme }) => theme.spacing[4]};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[12]} ${({ theme }) => theme.spacing[4]};
  text-align: center;
`;

const EmptyIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  color: ${({ theme }) => theme.colors.text.light};

  svg {
    width: 26px;
    height: 26px;
  }
`;

const EmptyTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const EmptyText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  max-width: 420px;
`;

const EmptyActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const SkeletonBar = styled.div<{ $width?: string; $height?: string }>`
  height: ${({ $height }) => $height ?? '14px'};
  width: ${({ $width }) => $width ?? '100%'};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const SkeletonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

// ==================== Empty state panel ====================
// Exported so GoalsManagementPage can reuse the same look for its
// "select a client" state (which has no clientId yet, so can't use this panel).

interface EmptyPanelProps {
  title: string;
  text: ReactNode;
  actions?: ReactNode;
}

export const EmptyPanel = ({ title, text, actions }: EmptyPanelProps) => (
  <Card>
    <EmptyState>
      <EmptyIcon>
        <TargetIcon />
      </EmptyIcon>
      <EmptyTitle>{title}</EmptyTitle>
      <EmptyText>{text}</EmptyText>
      {actions && <EmptyActions>{actions}</EmptyActions>}
    </EmptyState>
  </Card>
);

// ==================== Component ====================

export interface ClientGoalsPanelProps {
  clientId: string;
  clientName: string;
  /**
   * The caller's own gate on mutating actions (e.g. a drawer's read-only lock, or
   * simply `true` on a dedicated page with no other lock). Combined internally with
   * the `goals:manage`/`goals:import` permissions — the goals-specific permission
   * check lives entirely in this component so every consumer stays in sync.
   */
  canManage: boolean;
  /**
   * Overlay z-index for the Add/Edit/Change-status/Delete/Import modals. Raise this
   * above a surrounding overlay (e.g. ClientDetailDrawer's Panel) so the modals stack
   * on top of it; omit on a normal page where the default (theme.zIndex.modal) is fine.
   */
  zIndex?: number;
  /**
   * Notified whenever any of this panel's own modals opens/closes. A surrounding
   * overlay with its own Escape-to-close handler (e.g. ClientDetailDrawer) should
   * suppress that handler while this reports `true`, so Escape closes the goal
   * modal instead of also closing the surrounding overlay.
   */
  onOverlayOpenChange?: (isOpen: boolean) => void;
}

interface FormModalState {
  mode: 'create' | 'edit';
  goal?: GoalNode | null;
  parentNode?: GoalNode | null;
}

export function ClientGoalsPanel({
  clientId,
  clientName,
  canManage: canManageLock,
  zIndex,
  onOverlayOpenChange,
}: ClientGoalsPanelProps) {
  const { hasPermission } = useAuthStore();
  const canManage = canManageLock && hasPermission('goals:manage');
  const canImport = canManage && hasPermission('goals:import');

  const [formModal, setFormModal] = useState<FormModalState | null>(null);
  const [statusTarget, setStatusTarget] = useState<GoalNode | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<GoalNode | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);

  const isAnyModalOpen = !!formModal || !!statusTarget || !!deleteTarget || showImportModal;
  useEffect(() => {
    onOverlayOpenChange?.(isAnyModalOpen);
  }, [isAnyModalOpen, onOverlayOpenChange]);

  const { data: tree = [], isLoading } = useGoalTree(clientId);

  // Stable references so GoalTree/GoalTreeNode's React.memo isn't defeated by fresh
  // callback identities on every render.
  const openAdd = useCallback(
    (parent: GoalNode | null) => setFormModal({ mode: 'create', parentNode: parent }),
    [],
  );
  const openEdit = useCallback((goal: GoalNode) => setFormModal({ mode: 'edit', goal }), []);
  const closeFormModal = useCallback(() => setFormModal(null), []);
  const closeStatusModal = useCallback(() => setStatusTarget(null), []);
  const closeDeleteDialog = useCallback(() => setDeleteTarget(null), []);

  return (
    <Wrapper>
      {canManage && (
        <ActionsRow>
          {canImport && (
            <Button variant="secondary" leftIcon={<Upload />} onClick={() => setShowImportModal(true)}>
              Import Goals
            </Button>
          )}
          <Button variant="primary-blue" leftIcon={<Plus />} onClick={() => openAdd(null)}>
            Add Domain
          </Button>
        </ActionsRow>
      )}

      {isLoading ? (
        <Card>
          <SkeletonStack>
            <SkeletonBar $height="40px" />
            <SkeletonBar $height="40px" $width="90%" />
            <SkeletonBar $height="40px" $width="80%" />
          </SkeletonStack>
        </Card>
      ) : tree.length === 0 ? (
        <EmptyPanel
          title="No goals yet"
          text={`${clientName} doesn't have any goals yet. Add a domain manually or import from a CSV.`}
          actions={
            canManage && (
              <>
                <Button variant="secondary" leftIcon={<Plus />} onClick={() => openAdd(null)}>
                  Add Domain
                </Button>
                {canImport && (
                  <Button
                    variant="primary-blue"
                    leftIcon={<Upload />}
                    onClick={() => setShowImportModal(true)}
                  >
                    Import Goals
                  </Button>
                )}
              </>
            )
          }
        />
      ) : (
        <Card>
          <GoalTree
            nodes={tree}
            canManage={canManage}
            onAddChild={openAdd}
            onEdit={openEdit}
            onChangeStatus={setStatusTarget}
            onDelete={setDeleteTarget}
          />
        </Card>
      )}

      <GoalFormModal
        isOpen={!!formModal}
        onClose={closeFormModal}
        clientId={clientId}
        goal={formModal?.mode === 'edit' ? formModal.goal : null}
        parentNode={formModal?.mode === 'create' ? formModal.parentNode : null}
        createLevel={
          formModal?.mode === 'create'
            ? formModal.parentNode
              ? (NEXT_LEVEL[formModal.parentNode.level] ?? 'GOAL_TARGET')
              : 'DOMAIN'
            : undefined
        }
        zIndex={zIndex}
      />

      <ChangeStatusModal
        isOpen={!!statusTarget}
        onClose={closeStatusModal}
        clientId={clientId}
        goal={statusTarget}
        zIndex={zIndex}
      />

      <DeleteGoalDialog
        isOpen={!!deleteTarget}
        onClose={closeDeleteDialog}
        clientId={clientId}
        goal={deleteTarget}
        zIndex={zIndex}
      />

      {canImport && (
        <GoalImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          mode="single"
          clientId={clientId}
          zIndex={zIndex}
        />
      )}
    </Wrapper>
  );
}

export default ClientGoalsPanel;
